/**
 * Submissions v2 API client. Mirrors `src/api/v2/submission/*` from
 * orion-backend (PR #253).
 *
 * Always hits the real backend via `getJsonReal` / `sendJsonReal` —
 * `VITE_USE_MOCKS` is intentionally bypassed so `npm run dev` can keep
 * the broker autocomplete on mocks while submissions speak to staging.
 */
import type {
    SubmissionAdditionalInsuredWire,
    SubmissionContact,
    SubmissionListResponse,
    SubmissionLocationWire,
    SubmissionNamedInsuredWire,
    SubmissionRelativityWire,
    SubmissionStatus,
    SubmissionType,
    SubmissionWire,
    TriageViewParam,
} from "../../types/submission";
import type {
    AdditionalInsuredWritePayload,
    ContactWritePayload,
    LocationWritePayload,
    NamedInsuredWritePayload,
    RelativityWritePayload,
    SubmissionWritePayload,
} from "../adapters/submission";
import { getJsonReal, sendJsonReal, uploadMultipartReal } from "../client";

const BASE = "/v2/submissions";

// ────────────────────── Submissions (top-level) ─────────────────

export interface ListSubmissionsParams {
    offset?: number;
    limit?: number;
    status?: SubmissionStatus[];
    search?: string;
    /**
     * Triage view discriminator. `submission` = row per submission
     * (Deals), `facility` = row per facility. The response shape changes
     * accordingly (`SubmissionListResponse.view` echoes it back). Omitted
     * defaults to `submission` on the backend.
     */
    view?: TriageViewParam;
    /** Sort directive (`field` asc, `-field` desc) — forward-compat; the
     *  list endpoint doesn't accept it yet. */
    sort?: string;

    // ── Global Filters (all real backend query params) ──────────────
    brokerageId?: string;
    submissionTypes?: SubmissionType[];
    /** UW. */
    assigneeId?: number;
    /** Analyst. */
    approverId?: number;
    minRenewalDate?: string; // ISO YYYY-MM-DD
    maxRenewalDate?: string;
    minExpiringDeductible?: number;
    maxExpiringDeductible?: number;
    minNumFacilities?: number;
    maxNumFacilities?: number;
}

export function listSubmissions(params: ListSubmissionsParams = {}): Promise<SubmissionListResponse> {
    const qs = new URLSearchParams();
    if (params.view) qs.set("view", params.view);
    if (params.offset !== undefined) qs.set("offset", String(params.offset));
    if (params.limit !== undefined) qs.set("limit", String(params.limit));
    if (params.search) qs.set("search", params.search);
    if (params.sort) qs.set("sort", params.sort);
    if (params.status?.length) for (const s of params.status) qs.append("status", s);
    if (params.submissionTypes?.length) for (const t of params.submissionTypes) qs.append("submission_type", t);
    if (params.brokerageId) qs.set("brokerage_id", params.brokerageId);
    if (params.assigneeId !== undefined) qs.set("assignee_id", String(params.assigneeId));
    if (params.approverId !== undefined) qs.set("approver_id", String(params.approverId));
    if (params.minRenewalDate) qs.set("min_renewal_date", params.minRenewalDate);
    if (params.maxRenewalDate) qs.set("max_renewal_date", params.maxRenewalDate);
    if (params.minExpiringDeductible !== undefined) qs.set("min_expiring_deductible", String(params.minExpiringDeductible));
    if (params.maxExpiringDeductible !== undefined) qs.set("max_expiring_deductible", String(params.maxExpiringDeductible));
    if (params.minNumFacilities !== undefined) qs.set("min_num_facilities", String(params.minNumFacilities));
    if (params.maxNumFacilities !== undefined) qs.set("max_num_facilities", String(params.maxNumFacilities));
    const suffix = qs.toString();
    return getJsonReal<SubmissionListResponse>(`${BASE}/${suffix ? `?${suffix}` : ""}`);
}

export function getSubmission(id: string): Promise<SubmissionWire> {
    return getJsonReal<SubmissionWire>(`${BASE}/${encodeURIComponent(id)}`);
}

export function createSubmission(payload: SubmissionWritePayload): Promise<SubmissionWire> {
    return sendJsonReal<SubmissionWire>(`${BASE}/`, "POST", payload);
}

/**
 * Response shape of `POST /v2/submissions/from-upload`. The endpoint
 * is fully asynchronous: it returns 202 with the new submission's id
 * (under `submission_id`, NOT `id`) and the parse job's id. There is
 * no per-document payload anymore — poll
 * `GET /v2/submissions/upload/{job_id}` (see ./submission-upload.ts)
 * for classification and extraction progress; the extracted content
 * lands on the submission itself once the job completes.
 */
export interface SubmissionFromUploadResponse {
    submission_id: string;
    job_id: string;
    title: string;
    total_files: number;
}

/**
 * Create a brand-new submission directly from one or more uploaded
 * files. Wraps `POST /v2/submissions/from-upload` (multipart). The
 * submission exists immediately (navigate with `submission_id`);
 * extraction runs on the worker — poll the job for progress.
 */
export function createSubmissionFromUpload(files: File[], title?: string): Promise<SubmissionFromUploadResponse> {
    const formData = new FormData();
    for (const file of files) formData.append("files", file);
    if (title) formData.append("title", title);
    return uploadMultipartReal<SubmissionFromUploadResponse>(`${BASE}/from-upload`, formData);
}

export function patchSubmission(id: string, payload: SubmissionWritePayload): Promise<SubmissionWire> {
    return sendJsonReal<SubmissionWire>(`${BASE}/${encodeURIComponent(id)}`, "PATCH", payload);
}

export function deleteSubmission(id: string): Promise<void> {
    return sendJsonReal<void>(`${BASE}/${encodeURIComponent(id)}`, "DELETE");
}

// ──────────────────────────── Contact ─────────────────────────

function contactBase(submissionId: string): string {
    return `${BASE}/${encodeURIComponent(submissionId)}/contact/`;
}

export function getContact(submissionId: string): Promise<SubmissionContact> {
    return getJsonReal<SubmissionContact>(contactBase(submissionId));
}

export function putContact(submissionId: string, payload: ContactWritePayload): Promise<SubmissionContact> {
    return sendJsonReal<SubmissionContact>(contactBase(submissionId), "PUT", payload);
}

export function deleteContact(submissionId: string): Promise<void> {
    return sendJsonReal<void>(contactBase(submissionId), "DELETE");
}

// ──────────────────────── Named insureds ──────────────────────

function namedInsuredsBase(submissionId: string): string {
    return `${BASE}/${encodeURIComponent(submissionId)}/named-insureds`;
}

export function listNamedInsureds(submissionId: string): Promise<SubmissionNamedInsuredWire[]> {
    return getJsonReal<SubmissionNamedInsuredWire[]>(`${namedInsuredsBase(submissionId)}/`);
}

export function createNamedInsured(submissionId: string, payload: NamedInsuredWritePayload): Promise<SubmissionNamedInsuredWire> {
    return sendJsonReal<SubmissionNamedInsuredWire>(`${namedInsuredsBase(submissionId)}/`, "POST", payload);
}

export function patchNamedInsured(
    submissionId: string,
    namedInsuredId: string,
    payload: Partial<NamedInsuredWritePayload>,
): Promise<SubmissionNamedInsuredWire> {
    return sendJsonReal<SubmissionNamedInsuredWire>(
        `${namedInsuredsBase(submissionId)}/${encodeURIComponent(namedInsuredId)}`,
        "PATCH",
        payload,
    );
}

export function deleteNamedInsured(submissionId: string, namedInsuredId: string): Promise<void> {
    return sendJsonReal<void>(`${namedInsuredsBase(submissionId)}/${encodeURIComponent(namedInsuredId)}`, "DELETE");
}

// ───────────────────── Additional insureds ────────────────────

function additionalInsuredsBase(submissionId: string): string {
    return `${BASE}/${encodeURIComponent(submissionId)}/additional-insureds`;
}

export function listAdditionalInsureds(submissionId: string): Promise<SubmissionAdditionalInsuredWire[]> {
    return getJsonReal<SubmissionAdditionalInsuredWire[]>(`${additionalInsuredsBase(submissionId)}/`);
}

export function createAdditionalInsured(
    submissionId: string,
    payload: AdditionalInsuredWritePayload,
): Promise<SubmissionAdditionalInsuredWire> {
    return sendJsonReal<SubmissionAdditionalInsuredWire>(`${additionalInsuredsBase(submissionId)}/`, "POST", payload);
}

export function patchAdditionalInsured(
    submissionId: string,
    additionalInsuredId: string,
    payload: Partial<AdditionalInsuredWritePayload>,
): Promise<SubmissionAdditionalInsuredWire> {
    return sendJsonReal<SubmissionAdditionalInsuredWire>(
        `${additionalInsuredsBase(submissionId)}/${encodeURIComponent(additionalInsuredId)}`,
        "PATCH",
        payload,
    );
}

export function deleteAdditionalInsured(submissionId: string, additionalInsuredId: string): Promise<void> {
    return sendJsonReal<void>(`${additionalInsuredsBase(submissionId)}/${encodeURIComponent(additionalInsuredId)}`, "DELETE");
}

// ──────────────────────────── Locations ───────────────────────

function locationsBase(submissionId: string): string {
    return `${BASE}/${encodeURIComponent(submissionId)}/locations`;
}

export function listLocations(submissionId: string): Promise<SubmissionLocationWire[]> {
    return getJsonReal<SubmissionLocationWire[]>(`${locationsBase(submissionId)}/`);
}

export function createLocation(submissionId: string, payload: LocationWritePayload): Promise<SubmissionLocationWire> {
    return sendJsonReal<SubmissionLocationWire>(`${locationsBase(submissionId)}/`, "POST", payload);
}

export function patchLocation(
    submissionId: string,
    locationId: string,
    payload: Partial<LocationWritePayload>,
): Promise<SubmissionLocationWire> {
    return sendJsonReal<SubmissionLocationWire>(`${locationsBase(submissionId)}/${encodeURIComponent(locationId)}`, "PATCH", payload);
}

export function deleteLocation(submissionId: string, locationId: string): Promise<void> {
    return sendJsonReal<void>(`${locationsBase(submissionId)}/${encodeURIComponent(locationId)}`, "DELETE");
}

// ──────────────────────────── Relativity ──────────────────────

/**
 * Upsert the submission's relativity coefficients.
 * `PUT /v2/submissions/{id}/relativity/` — the only verb the endpoint
 * supports; relativity is read back embedded in the submission GET.
 */
export function putRelativity(
    submissionId: string,
    payload: RelativityWritePayload,
): Promise<SubmissionRelativityWire> {
    return sendJsonReal<SubmissionRelativityWire>(
        `${BASE}/${encodeURIComponent(submissionId)}/relativity/`,
        "PUT",
        payload,
    );
}
